import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {Box, Button, Grid} from '@mui/material';
import {useSnackbar} from 'notistack';
import React, {PropsWithChildren, useCallback, useEffect, useMemo} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {differenceInYears} from 'date-fns';
import AutoField, {ActionType, FormField} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew, {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import Loading from '../../components/Loading/Loading';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import useUpdatePrecursor from '../../hooks/useUpdatePrecursor';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {newUTCDate} from '../../utils/newUTCDate';
import {getContactFieldValues} from './getContactFieldValues';
import {SummarySection} from '../HandoverSummaryPage/SummarySection';
import {HeaderInvestmentPhilosophyFields} from './HeaderInvestmentPhilosophyFields';
import {HeaderVulnerabilityFields} from './HeaderVulnerabilityFields';
import {
  getContactAttribution,
  getSummaryReviewContactField,
} from '../PrecursorSummaryPage/headerFields';
import {postcursorFieldsType} from './postcursorFields';
import {useAccountVulnerabilities} from '../../components/AccountOverview/accountOverviewQueries';
import {AccountVulnerabilitiesFragment} from '../../schema-types/AccountVulnerabilitiesFragment';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {themeColors} from '../../theme/Theme';
import {ReviewStatusOneviewEnum} from '../../schema-types/globalTypes';
import {SaltusAny} from '../../utils/SaltusAny';
import {ErrorPage} from '../ErrorPage';

type HeaderFormProps = {
  accountDetails: AccountBasicFragment;
  vulnerabilities: AccountVulnerabilitiesFragment;
  review: ReviewDetailsFragment;
  updateLoading: boolean;
  readOnly?: boolean;
  showFooter: boolean;
};

export function PostcursorPage() {
  const {reviewDetails} = useReviewContext();
  const navigate = useNavigate();
  useEffect(() => {
    if (
      reviewDetails?.postcursorNotFinished &&
      reviewDetails.statusOneview !== ReviewStatusOneviewEnum.CLOSED
    ) {
      navigate({pathname: '../resilience-postcursor'});
    }
  }, [reviewDetails?.postcursorNotFinished, reviewDetails?.statusOneview]);
  if (
    reviewDetails?.postcursorNotFinished &&
    reviewDetails.statusOneview !== ReviewStatusOneviewEnum.CLOSED
  ) {
    return null;
  }
  return (
    <>
      <Box mt={2} />
      <HandoverStep />
      <PostcursorSummary showFooter />
      <Box mt={2} />
    </>
  );
}
export const PostcursorSummary: React.FC<PropsWithChildren<{showFooter: boolean}>> = ({
  children,
  showFooter = false,
}) => {
  const {account} = useAccountContext();
  const {enqueueSnackbar} = useSnackbar();
  const {reviewDetails} = useReviewContext();
  const {vulnerabilities} = useAccountVulnerabilities();

  const onError = useCallback(
    (msg: SaltusAny) =>
      enqueueSnackbar(`Could not update review. Please try again. Error: ${msg}`, {
        variant: 'error',
        autoHideDuration: null,
      }),
    [enqueueSnackbar]
  );

  const {updateReview, loading: updateLoading} = useUpdatePrecursor({
    reviewId: reviewDetails?.id,
    onSuccess: (x) => {},
    onError,
  });

  const initialValues = useMemo<Partial<postcursorFieldsType>>(() => {
    if (!reviewDetails || !account || !vulnerabilities) return {};
    return {
      reviewReason: reviewDetails.reviewReason,
      ...(reviewDetails.dateBooked ? {bookedMeetingDate: reviewDetails.dateBooked} : {}),
      ...(reviewDetails.dateScheduled
        ? {plannedMeetingDate: reviewDetails.dateScheduled}
        : {}),
      ...(reviewDetails.focus ? {reviewFocus: reviewDetails.focus} : {reviewFocus: ''}),
      ...(reviewDetails.meetingType ? {meetingType: reviewDetails.meetingType} : {}),
      opportunityValue: reviewDetails.opportunityValue || 0,
      name: reviewDetails.name?.replace(/undefined/gi, '') || '',
      reviewContact: getContactAttribution(account, reviewDetails),
      ...getContactFieldValues(vulnerabilities),
    };
  }, [reviewDetails, account, vulnerabilities]);

  if (!account || !vulnerabilities) {
    return <Loading entityName="Resilience" />;
  }

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  const formFields: FormField[] = [
    getSummaryReviewContactField(reviewDetails, account),
    {
      id: 'opportunityValue',
      fullWidth: true,
      label: 'Opportunity Value',
      type: ActionType.text,
      currency: true,
    },
  ];

  return (
    <>
      <PostcursorSummaryForm
        showFooter={showFooter}
        formFields={formFields}
        accountDetails={account}
        vulnerabilities={vulnerabilities}
        review={reviewDetails}
        submit={(values) => {
          // return updateReview(reviewId!, values, {inPrecursor: false});
        }}
        initialValueOverrides={initialValues}
        updateLoading={updateLoading}
      >
        {children}
      </PostcursorSummaryForm>
    </>
  );
};

const PostcursorSummaryForm = withAutoFormNew<
  postcursorFieldsType,
  PropsWithChildren<HeaderFormProps>
>(
  ({
    formFields,
    accountDetails,
    vulnerabilities,
    review,
    updateLoading,
    readOnly = true,
    children,
    showFooter,
  }) => {
    const {values} = useAutoForm<postcursorFieldsType>();
    const {submitForm} = useAutoForm();
    const headerFormInner = (
      <>
        <Grid container wrap="wrap">
          <FormReadOnly readOnly>
            {formFields?.map((field) => {
              return (
                <Grid item xs={6} xl={4} key={field.id}>
                  <HeaderField field={field} />
                </Grid>
              );
            })}
          </FormReadOnly>
        </Grid>
      </>
    );

    const colors = [themeColors.coral, themeColors.green, themeColors.yellow];

    const vulnerabilityFormInner = [
      vulnerabilities.primaryContact,
      vulnerabilities.secondaryContact,
    ].map((c, i) => {
      if (!c) {
        return null;
      }
      // const investmentContact = accountDetails.contacts?.find((x) => x.id === c.id);
      const longTermVulnerabilities = values.longTermVulnerabilityTypes[c.id];
      const eventVulnerabilities = values.eventVulnerabilityTypes[c.id];
      return (
        <Box key={c.id}>
          <SummarySection
            color={colors[i]}
            step="resilience"
            editable={false}
            test={`${c.simplifiedName} vulnerability`}
            title={`${c.simplifiedName}'s vulnerability details`}
          >
            <Box>
              <Box mt={1} />
              <Grid container wrap="wrap">
                <HeaderVulnerabilityFields contact={c} />
              </Grid>
              <Box mt={2} />
            </Box>
          </SummarySection>
          {/* Investment philosophy questions moved to risk */}
          {/* {investmentContact && (
            <SummarySection
              color={colors[i]}
              key={c.id}
              editable={false}
              step="resilience"
              title={`${c.simplifiedName} investment details`}
              test={`${c.simplifiedName} investment`}
            >
              <Grid key={c.id} p={1} container wrap="wrap" xs={12}>
                <HeaderInvestmentPhilosophyFields contact={investmentContact} />
              </Grid>
              <Box mt={1} />
            </SummarySection>
          )} */}
        </Box>
      );
    });

    return (
      <FormReadOnly readOnly>
        {children}
        <Grid container spacing={5}>
          <Grid item xs={3} lg={2}>
            <Box height="100%" display="flex" flexDirection="column">
              <SummarySection
                fullHeight
                step="resilience"
                editable={false}
                title="Edit resilience"
              >
                <Link to="../resilience-postcursor">
                  <Button variant="contained" color="secondary">
                    Edit resilience values
                  </Button>
                </Link>
              </SummarySection>
            </Box>
          </Grid>
          <Grid item xs={9} lg={10}>
            <SummarySection
              editable={false}
              step="meeting"
              title={`${accountDetails.name || ''} resilience details`}
            >
              <Box mt={1} />
              {headerFormInner}
            </SummarySection>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            {vulnerabilityFormInner}
            {accountDetails.isTrust && (
              <SummarySection
                // color={colors[i]}
                editable={false}
                step="resilience"
                title={`${accountDetails.name} investment details`}
                test={`${accountDetails.name} investment`}
              >
                <Grid p={1} container wrap="wrap" xs={12}>
                  <HeaderInvestmentPhilosophyFields contact={accountDetails} />
                </Grid>
                <Box mt={1} />
              </SummarySection>
            )}
          </Grid>
        </Grid>

        {showFooter ? (
          <HandoverFooterNavigation
            onExit={submitForm}
            onNext={submitForm}
            onNextLoading={updateLoading}
            nextIcon={<TrendingUpIcon />}
          />
        ) : null}
      </FormReadOnly>
    );
  },
  {enableReinitialize: true},
  {debugPosition: 'bottom'}
);

const HeaderField = ({field}: {field: FormField}) => {
  const [{value}, _, helpers] = useAutoField(field.id);
  const checkError = useMemo(() => {
    return ['primaryAtrDate', 'secondaryAtrDate'].includes(field.id);
  }, [field.id]);

  const ATRDateInvalid = useMemo(() => {
    if (checkError) {
      try {
        const atrDate = newUTCDate(value);
        if (Math.abs(differenceInYears(atrDate, newUTCDate())) >= 3) {
          helpers.setError('Over 3 years ago');
          return true;
        }
      } catch (err) {
        return true;
      }
    }
    return false;
  }, [checkError, value]);
  return (
    <Box display="flex" ml={1} mb={2} mr={1}>
      <AutoField error={ATRDateInvalid} field={field} />
    </Box>
  );
};
